import { createLogger } from 'shared/services/logger';
import { triggerUScopeEvent } from '../../_uScope';
import { Point, Rectangle } from '../../lib/objects/';
import { getIndexWith, isDefined, isUnDefined, reverseCLR, stripLeadingAtSign } from '../../lib/utility/helpers';
import us from './../../defines';
import { setIconState } from '../../toolbar';
import { addDrawFunctions } from './annotdraw';
import { createLayer } from './layer';
import LayerManager from './layer/layer-manager';
import Region from './regions/region';
import RegionList from './regions/regions';
import { get } from 'lodash';
const logger = createLogger('us:annotations-service');

// ________________________________________________________________________________________________
// annotations.js
//    This file contains the AnnotationsService object that manages annotation graphics.
//
//    Object Name: AnnotationsService
//    Overview: The AnnotationsService obect performs all annotation related functionality for a given viewport (image).
//
//    Associated Panel:
//        o cAnnotPanel
//
//    Dependencies and Supporting objects:
//        o Annotations are drawn by the raphael javascript library which operates on
//            HTML5 and non-HTML5 browser versions that we support.
//        o Region
//
//    Assumptions:
//        o todo
//
//    Limitations:
//        o Early development of annotations did not employ layers so all annotations were just a region list.  The annotation panel
//            navigation just walked through that list of regions.  Regions belonging to layers were implemented with IA however as
//            it stands now there is a list of layers and a list of regions.  Regions know their layer but a layer doesn't know its regions.
//            A lot of accessor code is wrapped around working between these two arrays.  Ideally the layer would create its regions.
//            This might not be a good idea though, it should be thoughtfully considered.
//
//    Terms Used:
//        o Abbreviations:
//            A-Layer - annotation layer containing regions that can be a color that is not the layer color
//            R-Layer - annotation layer containing the report region
//            IA - Image Analysis
//
//    Functionality:
//        Input/Output
//            o Read all annotation layers and regions for a given image from the server
//            o Write incremental annotation updates to the server as they occur
//                or write all annotation updates to the server on image close
//        Actions that are Annotation Specific
//            For the 'Annotation' layer
//                o Add, Delete, Select annotations
//                o Change an annotation's color, note
//                o Move, Redraw an annotation (in work, may not be exposed yet)
//            For 'Analysis' layers
//                o Display all input layers, regardless of where they were created
//                o Hide all output layers (because they serve no purpose without markup images and analysis results)
//                o Allow user to add regions to 'Annotation' layer only, in which they can change the color
//                o Disable the color picker on layers other than A-Layer or R-Layer
//        Interaction with Image Navigation
//            o Translation of annotations is performed by translate()
//            o Scaling of annotations is performed by scale()
//            o Rotation of annotations is performed by rotate()
//            o Next/Previous annotation navigation is performed by regSelectNextPrev
//
//    Data Structures:
//        There are 3 data structures of import here:
//        1) the annotation xml, fetched from the server, parsed and maintained in a jquery object
//            - colors maintained in BGR order here because that is the annotation format standard
//        2) the raphael 'paper', a canvas that is contained in a div covering the viewport,
//            that presents the annotation graphics
//        3) the array 'rfRegions' (in RegionList) that is used to relate between the annotation xml and the graphics paper
//            - colors maintained in RGB order here because that is natural to SVG graphics
//
//    HTML:
//        The annotations    div is created in anDivBuild() and is appended to its viewport div.
//
// Inter-Op Annotations: between uScope and ImageScope | WebScope
//        o Display all input layers, regardless of where they were created
//        o Hide all output layers (because they serve no purpose without markup images and analysis results)
//        o Allow user to add regions to 'Annotation' layer only, in which they can change the color
//        o Disable the color picker on layers other than A-Layer or R-Layer
//
//    References:
//        raphael.js is used to draw the vector graphics: http://raphaeljs.com
//        Scalable Vector Graphics: http://www.w3.org/TR/SVG/
//        SVG attribute index: http://www.w3.org/TR/SVG/attindex.html
// ________________________________________________________________________________________________
export default function AnnotationsService (uScope, viewport, legacyAnnotationProvider, textEditPanel, userConfig) {
  var self = this;

  // ..... to group fns in js parser, for ease of search (e.g. search for all 'CANNOT_XFORM')
  var CANNOT_INIT = this;
  var CANNOT_GET = this;
  var CANNOT_SET = this;
  var CANNOT_PAINT = this;
  var CANNOT_DRAW = this;
  var CANNOT_MARKUP = this;
  var CANNOT_DEBUG = this;
  var CANNOT_SAVE = this;
  var CANNOT_LAYER = this;
  var CANNOT_REGION = this;

  var isInitialized = false; // true when first annotation drawing is completed

  var jAnnotDiv;
  self.anDivRect = new Rectangle(0, 0, 0, 0); // anDiv's current geometry

  // ............................................ info
  CANNOT_GET.getMyImage = function () {
    return viewport;
  };
  CANNOT_GET.getImageId = function () {
    return viewport.getImageId();
  };
  CANNOT_DEBUG.toString = function (bDoShort, bReportResults) {
    var rtnStr = '';
    var theLayers = self.layers.getLayersArray();
    for (var lay = 0, len = theLayers.length; lay < len; lay++) { // directly access layer array for efficiency, could use getFirst(), getNext()
      if (theLayers[lay]) {
        rtnStr += '\n' + lay + ' Layer: ' + ((bDoShort) ? theLayers[lay].toStringShort() : theLayers[lay].toString(bReportResults));

        var layerId = theLayers[lay].get('Id');
        var regionArray = self.regions.findByProperty('layerId', layerId);
        for (var r = 0, rlen = regionArray.length; r < rlen; r++) {
          if (regionArray[r]) { rtnStr += '\n             ' + r + ' Region: ' + ((bDoShort) ? regionArray[r].toStringShort() : regionArray[r].toString(bReportResults)); }
        }
      }
    }
    return rtnStr;
  };

  us.anLayerTypesSupported[us.anLayerTypes.manual] = true; // initialize array of supported types
  us.anLayerTypesSupported[us.anLayerTypes.report] = true;
  us.anLayerTypesSupported[us.anLayerTypes.aafresult] = true;

  CANNOT_GET.getRegionsObj = function () {
    return self.regions;
  };

  CANNOT_GET.getLayersObj = function () {
    return self.layers;
  };

  // AnnotationsService tracks which layer is selected
  self.selLayerIdx = -1; // currently selected layer index in layers layerArray
  // AnnotationsService tracks which region is selected
  self.rfSelected = -1; // currently selected region index in rfRegions
  self.initialRfSelected = -1; // initial selected region in rfRegions - at time of read in

  CANNOT_GET.getRegionsArray = function () {
    return self.regions.getRegionsArray();
  };
  CANNOT_GET.getSelRegion = function () {
    return self.regions.getByRfId(self.rfSelected);
  }; // returns undefined if none selected
  CANNOT_GET.getSelRegionRfId = function () {
    return self.rfSelected;
  };
  CANNOT_GET.getSelType = function () {
    return self.regions.getType(self.rfSelected);
  };
  CANNOT_GET.getSelShape = function () {
    return self.regions.getShape(self.rfSelected);
  };
  CANNOT_GET.getSelRegionId = function () {
    return self.regions.getRegionId(self.rfSelected);
  };

  CANNOT_GET.getSelLayer = function () {
    return self.layers.getSelected();
  };

  /**
     * Given a layer, return corresponding 'spouse' layer (input or output)
     * @param {*} aLayer
     */
  CANNOT_GET.getSpouseLayer = function (aLayer) {
    let spouseLayer;
    if (aLayer) {
      var layerSpouseId = aLayer.get('InputAnnotationId'); // is this an output layer?
      if (layerSpouseId) {
        spouseLayer = self.layers.getLayerById(layerSpouseId); // then get the input layer
      } else { // this is an input layer
        var layerId = aLayer.get('Id');
        var layerArray = self.layers.findByProperty('InputAnnotationId', layerId); // does it have an associated output layer?
        if (layerArray.length) {
          spouseLayer = layerArray[0]; // note: not getting ALL output layers, assuming a single output layer here
        }
      }
    }
    return spouseLayer;
  };

  CANNOT_GET.getSelLayerId = function () {
    return self.layers.getSelectedId();
  };
  CANNOT_GET.getSelLayerIdx = function () {
    return self.layers.getSelectedIdx();
  };

  CANNOT_GET.getSelColor = function () {
    return self.regions.getRGB(self.rfSelected);
  };
  CANNOT_GET.getReportRegionId = function () {
    return self.regions.getReportRegionId();
  };

  // all on rfSelected
  CANNOT_GET.isWholeSlide = function () {
    return self.regions.isWholeSlide(self.rfSelected);
  };
  CANNOT_GET.isWholeSlideMarkup = function () {
    return self.regions.isWholeSlideMarkup(self.rfSelected);
  };

  // all on rfSelected
  CANNOT_MARKUP.markupIsInView = function (cr2View) {
    return self.regions.markupIsInView(self.rfSelected, cr2View);
  }; // returns true/false
  CANNOT_MARKUP.getMarkupInfo = function () {
    return self.regions.getMarkupInfo(self.rfSelected);
  }; // returns undefined if no markup
  CANNOT_MARKUP.hasMarkup = function () {
    return self.regions.hasMarkup(self.rfSelected);
  };

  // Define a highlight region for each region type.  This is better than creating and destroying
  // raphael objects for each selection as raphael does not reuse the removed ids.
  self.rfRegionHiLite = [];

  // ............................................ drawing
  self.reDrawIdx = 0;
  self.reDrawCnt = 0;
  self.reDrawPrevPt = undefined;

  CANNOT_DRAW.drawingIsStarted = function () {
    return (self.newRegion != undefined);
  };
  CANNOT_DRAW.drawingCompleted = function () {
    viewport.events.trigger('us:annotations:drawing-complete', self.newRegion, viewport.navModeGet());
    self.newRegion = undefined;
    viewport.navModeHalt();
  };

  // call back from ajaxGetAnnotations
  var paintInitTries = 0;
  CANNOT_INIT.init = function (jsonObj_) {
    if (!self.paper) {
      logger.error('AnnotationsService: Could not create graphics object; Disabling annotation drawing.');
      us.ENABLEANNOTCREATE = false;
      return;
    }

    paintInitTimer_();

    function paintInitTimer_ () {
      if (viewport.isInitialized()) { // if the first image refresh has completed
        paintInit_(); //   then paint the annotations (at zoom = 1.0)
      } else { // otherwise come back a little later and check the image paint status again
        if (++paintInitTries < 4) { //    but don't try forever
          setTimeout(paintInitTimer_, 1000);
        } else {
          logger.error('paintInitTimer: annotation first draw: nTries: ' + paintInitTries);
        }
      }
    }

    // ............................................  paintInit_()
    //    This function parses the initial annotations stored in jsonObj_ into layer and region objects
    //    and performs the initial rendering of the annotations.
    function paintInit_ () {
      self.paper.clear(); // remove all of the raphael graphics objects
      self.regions.clear(); // remove all regions
      var lastRfHandle = -1; // last region added
      var selRfHandle = -1; // selected region
      createHighlightRegions_(); // create a highlight region for each region type

      // note... really cLayers could take all of this in.....
      var jsAnnots = (jsonObj_.Annotations.Annotation_asArray) ? jsonObj_.Annotations.Annotation_asArray : [];

      for (var l = 0, jlen = jsAnnots.length; l < jlen; l++) {
        var jsLayer = jsAnnots[l]; // a layer :== [Annotation]
        var aLayer = createLayer(jsLayer, 'x2js'); // create this layer object from json

        // ignore un-supported or hidden layers and their regions
        if (!aLayer.get('isSupported') || !aLayer.get('isVisible')) {
          logger.warn('skipping layer', aLayer.get('id'), 'because it is not supported or invisible');
          continue;
        }
        self.layers.add(aLayer); // add aLayer to cLayers collection

        var jsRegions = jsLayer.Regions; // has RegionAttributeHeaders, Region[]
        var nRegions = jsRegions.Region_asArray ? jsRegions.Region_asArray.length : 0;

        for (var r = 0, rlen = nRegions; r < rlen; r++) {
          var jsRegion = jsRegions.Region_asArray[r];

          var aRegion = new Region(jsRegion, 'x2js', viewport, aLayer, self); // create this region

          if (aRegion.rfHandle) { // if successfully drawn
            self.regAdd(aRegion, false, false); // add the new region to the collection, don't save back to the db

            lastRfHandle = aRegion.rfHandle; // update last rfHandle
            var Selected = jsRegion._Selected === '1';
            if (aLayer.isSelected() && Selected) { // if layer and region is selected,
              selRfHandle = aRegion.rfHandle; // then this is the selected rfHandle
            }
          }
        }
      }

      isInitialized = true; // annotations initialized now; allows markupRefresh to execute
      initialView_(selRfHandle, lastRfHandle); // set the initial view according to the initial state parameters
      viewport.events.trigger('us:annotations:layers-loaded');
      return true;

      // ..................................................
      // closure functions
      // ............................................ createHighlightRegions_
      function createHighlightRegions_ () {
        for (var type = 0; type < us.kREGIONTYPES; type++) { // create a new region object of each region type
          self.rfRegionHiLite[type] = new Region({'type': type, isHighLight: true}, 'json', viewport, undefined, self);
        }
      }

      // This function sets the initial view according to the initial state parameters.
      //        Priority order of first view is derived from initial state parameters:
      //        1) initial region - if present, the code below responds
      //        then 2) initial x,y {optional z & mag} - if present, viewport already responded - just mag present works too
      //        then 3) last selected region - if a region exists, the code below responds
      //        then 4) center of image  - viewport's default and already performed
      function initialView_ (selRfHandle, lastRfHandle) {
        // save selected region rfId that came from the db
        if (selRfHandle !== -1) {
          self.initialRfSelected = selRfHandle.id;
        } else if (lastRfHandle != -1) {
          self.initialRfSelected = lastRfHandle.id;
        }

        var initState = viewport.initState;
        if (isDefined(initState.regionId)) { // if the initial state specified a region, go to it
          uScope.goToRegion(initState.imageId, initState.regionId); // returns false if the specified region is not found
        } else { // otherwise,  select either the region last selected or the last region if it existed
          if (selRfHandle != -1) {
            self.regSelect({
              rfId: selRfHandle.id,
              bSaveSel: false,
              bReposition: false
            });
          } else if (lastRfHandle != -1) {
            self.regSelect({
              rfId: lastRfHandle.id,
              bSaveSel: false,
              bReposition: false
            });
          }
        }

        if (!self.layers.getSelected()) {
          self.layers.selectByProperty('Name', us.AnnotLayerName);
        }
      }
    }
  };

  //    This function shows/hides a hovering note associated with a region.
  CANNOT_PAINT.regHoverShow = function (show, rfId) {
    if (show) {
      if (isDefined(self.hoverMemoHandle)) {
        self.hoverMemoHandle.show();
      }

      if (isDefined(self.hoverMemoBBoxHandle)) {
        self.hoverMemoBBoxHandle.show();
      }

      if (isUnDefined(rfId)) {
        const region = self.getSelRegion();
        rfId = region && region.rfId;
      }

      if (isDefined(rfId)) { // check if rdId has a truthy value... 0 is valid value
        self.rfDrawHover(rfId);
      }
    } else {
      if (isDefined(self.hoverMemoHandle)) {
        self.hoverMemoHandle.hide();
      }
      if (isDefined(self.hoverMemoBBoxHandle)) {
        self.hoverMemoBBoxHandle.hide();
      }
    }
  };
  // ............................................ show - This function shows/hides all annotation graphics
  CANNOT_PAINT.show = function (show) {
    if (jAnnotDiv) {
      if (show) jAnnotDiv.show();
      else jAnnotDiv.hide();
    }
  };
  // ............................................ rotate - This function rotate all annotation graphics (that remain upright during rotation)
  //    Note that the annotation div naturally rotates with the view.
  //    This function addresses rotation of individual shapes to angles other than that of the view.
  const rotateChange = function (angleDeg) {
    var rfRegions = self.getRegionsArray();

    for (var g = 0, glen = rfRegions.length; g < glen; g++) { // scale and position the graphics
      if (isDefined(rfRegions[g])) {
        var region = rfRegions[g];
        self.rfRotate(region, angleDeg);

        if (g == self.rfSelected) { // update the highlight on the selected region
          var type = +region.type;
          var hiRegion = self.rfRegionHiLite[type]; // get the region that highlights this type
          self.rfRotate(hiRegion, angleDeg);
        }
      }
    }
  };
    // ............................................ updatePosition
    //  This function sets the paper/canvas on-screen dimensions
    //    and position to that of the viewport. Call when the viewport size changes.
  function updatePosition ({left, top, width, height}) {
    self.anDivRect.x = left;
    self.anDivRect.y = top;
    self.anDivRect.w = width;
    self.anDivRect.h = height;
  }

  // ............................................ zoomChange - This function redraws all annotation graphics
  var zoomChange = function () {
    var paper = self.paper; // access to raphael for this image
    var rfRegions = self.getRegionsArray();

    for (var g = 0, glen = rfRegions.length; g < glen; g++) { // scale and position the graphics
      if (isDefined(rfRegions[g])) {
        self.rfDraw(paper, rfRegions[g], viewport.getViewZoom(), viewport.getViewAngle(true));
        if (g == self.rfSelected) { // update the highlight on the selected region
          self.regHighLight(rfRegions[g], true, viewport.getViewZoom(), viewport.getViewAngle(true));
        }
      }
    }
  };
    // ............................................ paintSelRegion - This function redraws the selected annotation region
  CANNOT_PAINT.paintSelRegion = function (bDoHighlight) {
    var region = self.getSelRegion(); // ref to the selected region
    var viewZoom = viewport.getViewZoom();
    var viewAngleDeg = viewport.getViewAngle(true);
    self.rfDraw(self.paper, region, viewZoom, viewAngleDeg);
    if (bDoHighlight != false) { // if not prohibited, update the highlight color of the selected region
      self.regHighLight(region, true, viewZoom, viewAngleDeg);
    }
  };

  // ............................................ setSelColor
  //    AnnotationsService.setSelColor - This function sets the color of the selected layer and region.
  CANNOT_SET.setSelColor = function (decRGB) {
    var decBGR = reverseCLR(decRGB);
    var viewZoom = viewport.getViewZoom();
    var viewAngleDeg = viewport.getViewAngle(true);

    // _____
    var aRegion = self.getSelRegion();
    var aLayer = self.getSelLayer();
    var alayerXML; // spouse layer is associated input or output layer
    var sLayer; // spouse layer is associated input or output layer
    var layerId;
    var regionXML;
    var slayerXML;

    if (aLayer && !aRegion) {
      layerId = aLayer.get('Id');
      sLayer = self.getSpouseLayer(aLayer);
      alayerXML = '<Annotation Id="' + layerId + '"' + ' LineColor="' + decBGR + '"' + '></Annotation>';

      if (sLayer) {
        alayerXML += '<Annotation Id="' + sLayer.get('Id') + '"' + ' LineColor="' + decBGR + '"' + '></Annotation>';
      }

      self.save({
        action: 'updateLayer',
        layerId: layerId,
        layerXML: alayerXML
      });
    } else if (aRegion) {
      layerId = aRegion.get('layerId');
      aLayer = self.layers.getLayerById(layerId);
      sLayer = self.getSpouseLayer(aLayer);
      // save the region and layer color to the database
      regionXML = '<Region Id="' + aRegion.get('regionId') + '"' + ' LineColor="' + decBGR + '"' + '></Region>';
      alayerXML = '<Annotation Id="' + layerId + '"' + ' LineColor="' + decBGR + '"' + '>'; // do not include closing tag
      self.save({
        action: 'updateRegionAndLayer',
        layerId: layerId,
        layerXML: alayerXML,
        regionId: aRegion.get('regionId'),
        regionXML: regionXML
      });

      if (sLayer) { // save the spouse layer to the database
        slayerXML = '<Annotation Id="' + sLayer.get('Id') + '"' + ' LineColor="' + decBGR + '"' + '></Annotation>';
        self.save({
          action: 'updateLayer',
          layerId: sLayer.get('Id'),
          layerXML: slayerXML
        });
      }
    } else {
      return logger.error('annot: setSelColor: no selected region, no selected layer, case not handled');
    }
    if (aLayer) aLayer.set('LineColor', decBGR); // set each layer's color
    if (sLayer) sLayer.set('LineColor', decBGR);

    // _____
    if (aLayer.get('isAnnotLayer') == false) { // if the layer is not the annotation layer,
      setRegionColor(layerId); // then color all the regions of this layer
      if (sLayer) { setRegionColor(sLayer.get('Id')); } // and if there exists a spouse layer, color its regions
    }

    if (aRegion) { // if there was a selected region,
      aRegion.setRGB(decRGB); // change the region color - necessary in case this is just a region on the annotation layer
      self.rfDraw(self.paper, aRegion, viewZoom, viewAngleDeg); // then repaint the region with the new color
      self.regHighLight(aRegion, true, viewZoom, viewAngleDeg); // and update the highlight color of the selected region
    }

    // ..................................................
    // closure functions

    // ............................................ setRegionColor
    function setRegionColor (layerId) {
      if (layerId) {
        var regionArray = self.regions.findByProperty('layerId', layerId); // then set the region color for all regions in the layer (no need to save to the db)
        for (var r = 0, rlen = regionArray.length; r < rlen; r++) {
          regionArray[r].setRGB(decRGB); // change the region color
          self.rfDraw(self.paper, regionArray[r], viewZoom, viewAngleDeg); // repaint the region with the new color
        }
      }
    }

    // ..................................................
  };

  // ............................................ saveAll
  // AnnotationsService.saveAll - This function saves all the annotations to the database.
  CANNOT_SAVE.saveAll = function () {
    logger.error('saveall:  not implemented');
  };
  // ............................................ save
  // AnnotationsService.save - This function saves the annotations to the database.
  //        Although it can execute a complete save of all annotations its primary work is in saving
  //        incremental updates to annotations.
  //
  // input parameters:
  //        parms.action - required; operation to perform; choices are:
  //                            saveAll -
  //                            addRegion -
  //                            delRegion -
  //                            updateRegion -
  //                            addLayer -
  //                            delLayer -
  //                            updateLayer -
  //                            updateRegionAndLayer -
  //
  //      The presence of the following parameters depend upon the action specified:
  //        parms.layerId - the id of the layer
  //        parms.regionId - the id of the region
  //        parms.layerXML - the xml content to attach to the <Annotations> node
  //        parms.regionXML - the xml content to attach to the <Regions> node
  //        parms.tag - a string to enclose in <tag> </tag> that data server will echo back - used to associate reqeusts and responses
  //
  //
  CANNOT_SAVE.save = async function (parms) {
    if (!uScope.canSaveAnnotations()) {
      return;
    }
    if (parms.action === 'saveAll') {
      return self.saveAll();
    }

    function errorResponse (reason) {
      logger.error(reason);
      return Promise.reject(reason);
    }
    let preamble = 'save: ' + parms.action + ': ';
    let xml;
    const imageId = stripLeadingAtSign(self.getImageId()); // id sent as a number, no leading '@'

    if (parms.action === 'delRegion') {
      if (isUnDefined(parms.layerId) || isUnDefined(parms.regionId)) {
        errorResponse(preamble + ' layer or region id undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode><DeletedAnnotations><Annotation Id="19"><Regions><Region Id="390"/></Regions></Annotation></DeletedAnnotations></AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<DeletedAnnotations>' +
                '<Annotation Id="' + parms.layerId + '"' + '>' +
                '<Regions><Region Id="' + parms.regionId + '"' + '/></Regions>' +
                '</Annotation></DeletedAnnotations></AnnotationsNode>';
    } else if (parms.action === 'delLayer') {
      if (isUnDefined(parms.layerId)) {
        errorResponse(preamble + ' layer id undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode><DeletedAnnotations><Annotation Id="19"></Annotation></DeletedAnnotations></AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<DeletedAnnotations>' +
                '<Annotation Id="' + parms.layerId + '"' + '>' +
                '</Annotation></DeletedAnnotations></AnnotationsNode>';
    } else if (parms.action === 'addRegion') {
      if (isUnDefined(parms.layerId) || isUnDefined(parms.regionXML)) {
        errorResponse(preamble + ' layer or region id or regionXML undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode>
      //        <Tag>echoed information here</Tag> // return original, negative regionId so assigned Id can be replaced
      //        <Annotations><Annotation Id="17">
      //            <Regions><Region Id="-474" ... DisplayId="-474"> ... <Region></Regions>
      //        </Annotation></Annotations>
      //    </AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<Annotations>' +
                '<Annotation Id="' + parms.layerId + '"' + '>' +
                '<Regions>' + parms.regionXML + '</Regions>' +
                '</Annotation></Annotations></AnnotationsNode>';
    } else if (parms.action === 'updateRegion') {
      if (isUnDefined(parms.layerId) || isUnDefined(parms.regionXML)) {
        errorResponse(preamble + ' layer or region id or regionXML undefined');
      }
      //    <ImageId>10</ImageId>
      //    <AnnotationsNode><Annotations><Annotation Id="17">
      //        <Regions><Region Id="12" ... > ... <Region></Regions>
      //    </Annotation></Annotations></AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<Annotations>' +
                '<Annotation Id="' + parms.layerId + '"' + '>' +
                '<Regions>' + parms.regionXML + '</Regions>' +
                '</Annotation></Annotations></AnnotationsNode>';
    } else if (parms.action === 'addLayer') {
      if (isUnDefined(parms.layerXML)) {
        errorResponse(preamble + ' layer xml undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode>
      //        <Tag>echoed information here</Tag>
      //        <Annotations><Annotation Id="-17" ...></Annotation></Annotations>
      //    </AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                '<Tag>' + parms.tag + '</Tag>' + // return original, negative layerId so assigned Id can be replaced
                '<Annotations>' + parms.layerXML + '</Annotations></AnnotationsNode>';
    } else if (parms.action === 'updateLayer') {
      if (isUnDefined(parms.layerXML)) {
        errorResponse(preamble + ' layer xml undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode><Annotations><Annotation Id="-17" ...></Annotation></Annotations></AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<Annotations>' +
                parms.layerXML +
                '</Annotations></AnnotationsNode>';
    } else if (parms.action === 'updateRegionAndLayer') {
      if (isUnDefined(parms.layerId) || isUnDefined(parms.regionId) || isUnDefined(parms.layerXML) || isUnDefined(parms.regionXML)) {
        errorResponse(preamble + ' layer or region id or XML undefined');
      }

      //    <ImageId>10</ImageId>
      //    <AnnotationsNode><Annotations><Annotation Id="17" Selected="1">
      //        <Regions><Region Id="12" ... > ... <Region></Regions>
      //    </Annotation></Annotations></AnnotationsNode>
      xml = '<ImageId>' + imageId + '</ImageId>' +
                '<AnnotationsNode>' +
                ((parms.tag) ? '<Tag>' + parms.tag + '</Tag>' : '') +
                '<Annotations>' +
                parms.layerXML + // note that closing tag must not be present
                '<Regions>' + parms.regionXML + '</Regions>' +
                '</Annotation></Annotations></AnnotationsNode>';
    }

    if (!xml) {
      return errorResponse(`${preamble} could not construct valid request with given params: "${JSON.stringify(parms)}"`);
    }

    let response = await legacyAnnotationProvider.putAnnotations3(xml);

    if (parms.action === 'addRegion') {
      const annotation = response.data.AnnotationsNode[0].Annotations[0].Annotation[0];
      const imageId = '@' + annotation.ImageId[0]; // Cindy always preceded imageId number with @

      // I left the original code commented out so you can compare to the original; obviously clean up before committing :)
      const jTag = response.data.Tag[0]; // get the returned tag
      const layerId = Number(get(annotation, '$.Id'));
      const oRegionId = parseInt(jTag, 10); // the original, negative region id
      const jRegion = annotation.Regions[0].Region[0]; // s/b [  ]
      const regionId = Number(get(jRegion, '$.Id')); // the assigned region id

      // replace the temporary negative regionId with the one assigned by dataserver
      self.regions.addRegionResponse({imageId, layerId, negId: oRegionId, assignedId: regionId});
    } else if (parms.action === 'addLayer') {
      const annotation = response.data.AnnotationsNode[0].Annotations[0].Annotation[0];
      return annotation;
    }
  };

  // ............................................ regSelect
  // AnnotationsService.regSelect - This function deselects the currently selected graphics object and
  //                selects the specified new one.
  //
  //    input parms:
  //        rfId:            required if regionId not provided, index into rfRegions to select
  //        regionId:        required if rfId not provided, id of region to select
  //                note: if both rfId and regionId are provided, rfId is used
  //
  //        bReposition:    optional, default true, reposition the view to center on the selection at the region zoom
  //        bDoZoom:        optional, default true, zoom to region's zoom
  //        bSaveSel:        optional, default true, update the database - this region/layer selected
  //                        - note that this may be overridden by us.ENABLEANNOTSELSAVE in which case it will not be saved
  CANNOT_REGION.regSelect = function (parms = {bReposition: true, bDoZoom: true, bSaveSel: true}) {
    var rfRegions = self.getRegionsArray();

    var rfId = parms.rfId;
    if (!rfId && parms.regionId) { // if rfId not defined and regionId is, find rfId of that regionId in rfRegions
      rfId = getIndexWith(rfRegions, 'regionId', parms.regionId); // returns -1 if regionId is not in rfRegions
    }

    if (!isValidArrayIndex(rfRegions, rfId)) {
      logger.warn('regSelect: Invalid rfid', rfId);
      return false;
    }

    var region = rfRegions[rfId]; // newly selected region
    if (region.InputRegionId) { // Prefer input regions if they're available.
      region = self.regions.getByRegionId(region.InputRegionId);
      rfId = region.rfId;
    }
    var bReposition = parms.bReposition;
    var bSaveSel = us.ENABLEANNOTSELSAVE; // if saving the selection is disabled, disregard parms.bSaveSel
    if (bSaveSel) {
      bSaveSel = parms.bSaveSel;
    }

    var curZoom = viewport.getViewZoom();
    if (self.rfSelected !== rfId) { // if the passed region is not already selected
      // deselection of the previous (current) graphic
      if (self.rfSelected >= 0 && rfRegions[self.rfSelected] != undefined) { // if the current select is valid
        var prevRegion = rfRegions[self.rfSelected];
        self.regHighLight(prevRegion, false, region.zoom, viewport.getViewAngle(true));

        if (uScope.canSaveAnnotations() && bSaveSel) { // send a layer and region update message
          prevRegion.onSelectChange(false);
        }
      }

      self.rfSelected = rfId;
      self.regHighLight(region, true, curZoom, viewport.getViewAngle(true)); // FB 25001: Removed conditional zoom selection to ensure highlight is always shown on top of the selected region

      if (uScope.canSaveAnnotations() && bSaveSel) { // send a layer and region update message
        region.onSelectChange(true);
      }

      if (isInitialized && region.regionId >= 0) {
        viewport.events.trigger('us:annotations:region-selected', region);

        // This event is triggered solely for the pindrop navigator sync. TODO: rework pdn
        viewport.events.trigger('us:annotations:region-change', {
          imageId: region.imageId,
          regionId: region.regionId,
          layerId: region.layerId,
          anShape: (region.isReportRegion) ? 'report' : region.shape,
          action: 'sel'
        });
      }
    }

    if (bReposition) { // if specified to reposition the view (this is default behavior)
      var pt = region.getCenter();

      var bDoMove = false;
      // if region center is within the view, no need to reposition
      var cpViewCenter = viewport.getViewCenter(); // in zoom image coords
      var ptViewCenter = new Point(cpViewCenter.x * curZoom, cpViewCenter.y * curZoom);
      var dx = Math.abs(pt.x - ptViewCenter.x);
      var dy = Math.abs(pt.y - ptViewCenter.y);
      if (dx > viewport.getViewWidth() / 2 || dy > viewport.getViewHeight() / 2) { bDoMove = true; }

      var bDoZoom = (parms.bDoZoom != undefined) ? parms.bDoZoom : (Math.abs(region.zoom - curZoom) > 0.1);

      if (bDoMove) { // if the region isn't near the center of the view
        viewport.moveAbsoluteCenter(pt.x, pt.y, {animate: true}); // then center it
      }
      if (bDoZoom) { // if the region's zoom isn't close to the current zoom
        viewport.zoomAbsolute(region.zoom); // then change zooms
      }
    }

    return true;
  };

  // ............................................ regSelectToCenter
  // AnnotationsService.regSelectToCenter - This function zooms/centers the view at the currently selected region.
  //
  //    parameters:
  //        opt_bDoZoom:    optional, boolean, default true, if false the view zoom is not changed
  //
  CANNOT_REGION.regSelectToCenter = function (opt_bDoZoom) {
    var region = self.getSelRegion(); // ref to the selected region
    if (region) {
      var pt = region.getCenter(); // selected region's center pt
      if (opt_bDoZoom != false) { viewport.zoomAbsolute(region.zoom); }
      viewport.moveAbsoluteCenter(pt.x, pt.y, {animate: true});
    }
  };

  // ............................................ regSelectSaveAtClose
  // AnnotationsService.regSelectSaveAtClose - This function is intended to be used when closing an image
  //        whose annotations were auto updated with the exception of the select/deselect state.
  //        The user rights permission is checked in this function.
  CANNOT_REGION.regSelectSaveAtClose = function () {
    // if the user has rights to save annotations and the initial selection isn't the current selection
    if (uScope.canSaveAnnotations() && self.initialRfSelected != self.rfSelected) {
      var rfRegions = self.getRegionsArray();
      var region;

      if (isValidArrayIndex(rfRegions, self.initialRfSelected)) {
        region = rfRegions[self.initialRfSelected];
        region.onSelectChange(false);
      }
      if (isValidArrayIndex(rfRegions, self.rfSelected)) {
        region = rfRegions[self.rfSelected];
        region.onSelectChange(true);
      }
    }
  };

  // ............................................ regSelectNextPrev
  // AnnotationsService.regSelectNextPrev - This function selects the first, next, previous or last annotation.
  //                    It returns true if it successfully selected an annotation, otherwise false.
  //    passed parameters:
  //        action:            required, string, "first", "next", "last", "previous", "current"
  //        bReposition:    optional, default true, reposition the view to center on the selection
  //        startIndex:     optional, default null, to recalculate the currentRegionIndex after a region delete
  //
  CANNOT_REGION.regSelectNextPrev = function (action, bReposition = true, startIndex = null) {
    // Limit navigation to input regions only
    const rfRegions = self.getRegionsArray().filter(r => r && !r.InputRegionId);
    if (rfRegions.length === 0 || self.rfSelected < 0) {
      return;
    }

    let regionToSelect;
    let currentRegionIndex = rfRegions.indexOf(self.getSelRegion());
    const lastRegionIndex = rfRegions.length - 1;

    // After a region delete (only), recalculate the currentRegionIndex in order to select a new region (next available region in the list)
    if (startIndex !== null) {
      currentRegionIndex = startIndex === 0 ? lastRegionIndex : startIndex - 1; // every region other than the first one to go back one step hypothetically
    }

    if (action === 'current') {
      self.regSelectToCenter(); // if the current region is selected, select it which puts it in view
      return;
    } else if (action === 'first' || (action === 'next' && currentRegionIndex === lastRegionIndex)) {
      regionToSelect = rfRegions[0];
    } else if (action === 'last' || (action === 'previous' && currentRegionIndex === 0)) {
      regionToSelect = rfRegions[lastRegionIndex];
    } else {
      regionToSelect = rfRegions[currentRegionIndex + (action === 'next' ? 1 : -1)];
    }

    return self.regSelect({rfId: regionToSelect.rfId, bReposition});
  };

  // ............................................ regSelectDelete
  //    AnnotationsService.regSelectDelete - This function deletes the selected annotation
  CANNOT_REGION.regSelectDelete = async function () {
    const region = self.getSelRegion();
    await region && self.regDelete(region.get('regionId'));
  };

  // ............................................ layerReInit
  // This function is called after analysis is run to get the aafresult layer and its regions.
  // Optionally it will select the output region associated with the passed opt_inputRegionId
  // Optionally it will call the passed opt_cbLayerReInit with the layerId when the layer data has been loaded/parsed.
  CANNOT_LAYER.layerReInit = function (imageId, layerId) { /// //////////////consider grouping with ther layer fns, consider just doing this inside cLayers?????????
    var xml = '<ImageId>' + imageId + '</ImageId>' + '<AnnotationId>' + layerId + '</AnnotationId>';
    viewport.ajax.doDATASVRREQ(cbSuccessFn, cbFailureFn, 'GetAnnotations2', xml);

    // ..................................................
    // closure functions
    // ............................................ cbSuccessFn()
    // note the similarity here to paintInit
    function cbSuccessFn (data, textStatus, jqXHR) {
      const x2js = new X2JS();
      const jsonObj_ = x2js.xml_str2json(data); // convert input xml to json object
      const jsLayer = jsonObj_.GetAnnotations2Response.AnnotationsNode.Annotations.Annotation;
      const layer = createLayer(jsLayer, 'x2js'); // create this layer object from json
      self.layers.replace(layer); // replace layer to cLayers collection

      const jsRegions = jsLayer.Regions; // has RegionAttributeHeaders, Region[]
      const nRegions = jsRegions.Region_asArray ? jsRegions.Region_asArray.length : 0;
      const updatedRegions = {};

      // todo: regions holds <RegionAttributeHeaders> where the ID here is that Attribute Name for a region...

      for (let r = 0, rlen = nRegions; r < rlen; r++) {
        const jsRegion = jsRegions.Region_asArray[r];

        const bRegion = self.regions.find(r => r && r.layerId === layerId && (r.regionId === jsRegion._Id || r.displayId === jsRegion._DisplayId));

        if (bRegion) {
          const oldRegionId = bRegion.regionId;
          bRegion.updateProps(jsRegion, 'x2js', layer);
          updatedRegions[oldRegionId] = bRegion;
        } else {
          const aRegion = new Region(jsRegion, 'x2js', viewport, layer, self);
          if (aRegion.rfHandle) { // if successfully drawn
            self.regAdd(aRegion); // add the new region to the collection, don't save back to the db
          }
        }
      }

      if (Object.keys(updatedRegions).length) {
        viewport.events.trigger('us:annotations:layer-reinitialized', {
          imageId: viewport.rawImageId,
          layer,
          regions: updatedRegions
        });
      }
    }

    // ............................................ cbFailureFn()
    function cbFailureFn (jqXHR, textStatus, errorThrown) {
      logger.error('Error calling GetAnnotations2:', jqXHR, textStatus, errorThrown);
      triggerUScopeEvent(us.evAJAXERROR, {imageId: self.getImageId(), request: 'GetAnnotations2', jqXHR: jqXHR});
    }

    // ..................................................
  };

  // ............................................ layerIsNamed
  // AnnotationsService.layerIsNamed - This function returns true if the passed layerId is that of the layer named 'LayerName'.
  //                This is useful to test if one is on the us.AnnotLayerName or us.ReportLayerName.
  CANNOT_LAYER.layerIsNamed = function (layerId, LayerName) {
    return (self.layers.getLayerNameById(layerId) === LayerName);
  };

  /**
   * Copys a region.
   * potential issues:  adding to current layer, what if in a different image?  region needs to add to RegionList
   * @param cpBaseCenter: Point, base coord to center region at
   * @param regionToCopy
   */
  CANNOT_REGION.regCopyTo = function (cpBaseCenter, regionToCopy) {
    if (!uScope.canAnnotate()) {
      return;
    }
    var paper = self.paper; // access to raphael for this image

    const layer = self.layers.getLayerById(regionToCopy.layerId);
    const aRegion = new Region({
      layerId: regionToCopy.layerId,
      type: regionToCopy.type,
      angleDeg: regionToCopy.angleDeg,
      zoom: regionToCopy.zoom,
      decRGB: regionToCopy.decRGB,
      isReportRegion: regionToCopy.isReportRegion,
      pts: regionToCopy.copyPts()
    },
    'json',
    viewport,
    layer,
    self);

    aRegion.centerAt(cpBaseCenter); // modify region points to center on input center pt

    self.rfDraw(paper, aRegion, viewport.getViewZoom(), viewport.getViewAngle(true)); // draw this graphics object
    self.regAdd(aRegion, true, true); // add the new region to the collection and save back to the db

    self.regSelect(aRegion); // select the new region to ensure mouse motion isn't applied to old region.
  };

  CANNOT_REGION.regCreate = ({layerId, type, angleDeg, zoom, points, colorRgb = us.getRandomColor(), isReportRegion = false}, isExported = false) => {
    const layer = self.layers.getLayerById(layerId);
    const region = new Region({
      layerId,
      type,
      angleDeg,
      zoom,
      decRGB: colorRgb,
      isReportRegion,
      pts: points
    },
    'json',
    viewport,
    layer,
    self,
    isExported);

    self.rfDraw(self.paper, region, zoom, angleDeg);

    return self.regAdd(region, true, true);
  };

  CANNOT_REGION.regAdd = async function (aRegion, bDoSave, bDoSelect) {
    await self.regions.add(aRegion, bDoSave);

    // toconsider: this really doesn't belong here!!! I had to wait for the region to have a valid rfHandle but still... ol3 and all....perhaps the fn should move up to here
    aRegion.addEventHandlers({click: true, hover: true, textclick: true}); // register the click and hover event handlers for this region

    // unselect the currently selected object and select this one (but don't reposition the view)
    if (bDoSelect) { self.regSelect({rfId: aRegion.rfId, bReposition: false, bSaveSel: false}); }
  };

  // ...............................................................addRegEventHandlerHighlight
  // .................................................................add event listener to hightlight element
  CANNOT_REGION.addRegEventHandlerHighlight = function (aRegion) {
    aRegion.rfHandle.node.addEventListener('contextmenu', (event) => {
      viewport.events.trigger('us:annotations:show-context-menu', {event, region: self});
    });

    // add eventHandle only once
    aRegion.hasEventHandlerHighlight = true;
  };

  // ............................................ regDelete
  //    AnnotationsService.regDelete - This function deletes the passed region object and updates, informs components and listeners.
  CANNOT_REGION.regDelete = async function (regionId) {
    let didNotSaveResponse = Promise.resolve(false);
    if (!uScope.canAnnotate()) {
      return didNotSaveResponse;
    }

    const region = self.regions.getByRegionId(regionId);
    if (region) {
      if (region.get('isTuningRegion')) {
        return didNotSaveResponse;
      }

      const rfRegions = self.getRegionsArray().filter(r => r && !r.InputRegionId);
      const deletedIndex = rfRegions.indexOf(region);

      self.regHighLight(region, false);
      const rfId = region.get('rfId');
      const wasDeleted = await self.regions.del(rfId);

      if (!wasDeleted) {
        return didNotSaveResponse;
      }

      // if the deleted region had be the selected region
      // select the next region but do not reposition the view
      // pass the deletedIndex along to be able to select the next region correctly
      if (self.rfSelected == rfId && !self.regSelectNextPrev('next', false, deletedIndex)) {
        // insure that it is understood that nothing is selected
        self.rfSelected = -1;
        viewport.events.trigger('us:annotations:region-selected', null);
      }

      // todo: i wonder that the hover event handler s/b removed and if that would eliminate the need for this call and fn
      self.regHoverShow(false); // in case this region had hover note visible
      return Promise.resolve(true);
    } else {
      logger.warn('Could not locate region', regionId);
    }
  };

  // ............................................ regMove
  CANNOT_REGION.regMove = function (cpBaseDist) {
    var region = self.getSelRegion(); // ref to the selected region
    var ptList = region.getPts();

    for (var i = 0, len = ptList.length; i < len; i++) {
      ptList[i].addPoint(cpBaseDist);
    }
    self.paintSelRegion(us.DO_HIGHLIGHT); // force a repaint of this region and paint the highlight
  };

  // ............................................ regMoveCenter
  CANNOT_REGION.regMoveCenter = function (cpBaseCenter) {
    var region = self.getSelRegion(); // ref to the selected region
    region.centerAt(cpBaseCenter); // modify region points to center on input cpCenter
    self.paintSelRegion(us.DO_HIGHLIGHT); // force a repaint of this region but do not paint the highlight
  };

  // ............................................ regAddNote
  //    regAddNote -
  CANNOT_REGION.regAddNote = function (note) { // add a note to the selected region
    setIconState($('.' + us.APNOTE), us.APNOTE, 'enabled');
    var region = self.getSelRegion();
    if (region) {
      region.addNote(note); // update note and save to database
      self.regHoverShow(true, region.rfId); // show the hover note on screen
    }
  };

  CANNOT_REGION.onAddImageText = function (crsrPos, onImageText) {
    setIconState($('#' + us.APANNOTTEXT), us.APANNOTTEXT, 'enabled');
    if (!onImageText || !uScope) {
      // do nothing if the text is undefined
      return;
    }

    if (onImageText[onImageText.length - 1] == '\n') { // strip off trailing carriage return
      onImageText = onImageText.substr(0, onImageText.length - 1);
    }

    onImageText = onImageText.substring(0, us.textEditSpecs.maxChars); // limit number of characters

    if (crsrPos) { // if crsrPos is defined, add the region at crsrPos
      if (onImageText.length > 0) { // but don't add region if it has no content
        self.startNew(us.eAnTypes.text, crsrPos, viewport.getViewZoom(), viewport.getViewAngle(true), onImageText);
      }
    } else { // else if no defined crsrPos the region text is being updated
      if (onImageText.length > 0) { // only change text if there is something to change (do not allow total clearing of on image text)
        self.regAddNote(onImageText); // update region and database
        self.rfDraw(self.paper, self.getSelRegion()); // draw updated region
      }
    }
  };

  addDrawFunctions(uScope, viewport, this, textEditPanel, userConfig);

  /**
     * This function selects or creates the appropriate layer for the passed annotation type and returns the layerId to
     * assign to the region. If a new region is being added to the report layer and there is already a report region,
     * that region is deleted.
     * @param anType
     * @returns {number} ID of the selected layer. A negative Id is returned if no layer can be selected.
     */
  self.chooseLayer = function (anType) {
    const layers = self.getLayersObj();
    let rtnSelLayerId = -1;

    if (anType == us.eAnTypes.report) { // if anType is report, select/created report layer
      const reportLayer = layers.getReportLayer();
      if (reportLayer) {
        rtnSelLayerId = reportLayer.get('Id');
        layers.deleteRegions(rtnSelLayerId); // remove any existing regions from the report layer (since only one is allowed to exist)
      } else {
        rtnSelLayerId = getSpecialLayerIdOrCreateIt(us.ReportLayerName);
      }
    } else { // otherwise we are creating an annotation that is not a report region
      const selLayer = layers.getSelected();
      if (selLayer) { // if selected layer is valid
        if (selLayer.get('isReportLayer') == false) { // if selected layer is NOT a report layer
          if (selLayer.get('Type') != us.anLayerTypes.aafresult) {
            rtnSelLayerId = selLayer.get('Id'); // as long as the currently selecte layer isn't a result layer, it will be used
          } else {
            rtnSelLayerId = selLayer.get('InputAnnotationId'); // but if aafresult layer is currently selected, switch to its parent layer
          }
        }
      }
    }

    if (rtnSelLayerId < 0) { // if the layerId is still not valid then just use/create annotation layer
      rtnSelLayerId = getSpecialLayerIdOrCreateIt(us.AnnotLayerName);
    }

    layers.selectByProperty('Id', rtnSelLayerId); // now, finally, select that layer; note that passing in a -1 deselects all layers

    return rtnSelLayerId;

    /**
         * This function returns the selected layerId or -1 if not able to select it and requested it be created
         * @param layerName
         * @private
         */
    function getSpecialLayerIdOrCreateIt (layerName) {
      let layerId = layers.getLayerIdByName(layerName);
      if (layerId < 0) { // if the layer does not exist,
        layerId = layers.getProvisionalLayerId();
        layers.createAddSaveSpecial(layerName); // and create this special layer
      }
      return layerId;
    }

    // ..................................................
  };

  (async function construct () {
    // Make this event global so legacy PDN / VIL can access it. This "change" event is kind of weird as it can represent many different types of events.
    // It was originally published on a jQuery DOM event channel but the event itself can stand refacotring more intelligently.
    viewport.events.publicize('us:annotations:region-change', 'us:annotations:layers-loaded');
    self.regions = new RegionList(viewport.events, self); // create the one region collection; so much user interaction is on a region, direct ref to region is expedient
    self.layers = new LayerManager(uScope, viewport.events, self);

    jAnnotDiv = $('<div/>', {
      'class': us.clsANNOTDIV + ' ' + us.clsGRAPHICS
    });

    // create the raphael paper tag inside the annot div
    self.paper = Raphael(jAnnotDiv[0], '100%', '100%');

    // NOTE: the following are raphael rendering bug fixes
    self.paper.renderfix(); // Fixes the issue of Firefox and IE9 regarding subpixel rendering.
    self.paper.safari(); // There is an inconvenient rendering bug in Safari (WebKit): sometimes the rendering should be forced.

    viewport.renderer.addLayer({
      container: jAnnotDiv[0],
      updatePosition,
      zoomChange,
      rotateChange
    });

    viewport.events.on('us:annotations:region-removed', region => {
      self.regHighLight(region, false);
    });

    viewport.ajax.queryImageServer('GETANNOTATIONS')
      .then((data, status, jqXhr) => {
        const x2js = new X2JS();
        const json = x2js.xml_str2json(jqXhr.responseText);
        self.init(json);
      })
      .fail((error) => {
        logger.error('annotations-service: Failed to get annotations', error); // eslint-disable-line no-console
        triggerUScopeEvent(us.evAJAXERROR, {
          imageId: self.getImageId(),
          request: 'GETANNOTATIONS',
          jqXHR: error
        });
      });
  }());
}

// getAnTypeButtonClass - This function returns the button element class of the annotation type for the passed region or undefined
us.getAnTypeButtonClass = function (aRegion) {
  var className;
  var anType = isDefined(aRegion) ? +aRegion.get('type') : -1;

  if (anType >= 0) { // get the element id of the selected annotation type
    if (anType === us.eAnTypes.pen) className = us.APANNOTPEN;
    else if (anType === us.eAnTypes.negpen) className = us.APANNOTNEGPEN;
    else if (anType === us.eAnTypes.rect && aRegion.get('isReportRegion')) className = us.APANNOTREPORT;
    else if (anType === us.eAnTypes.rect) className = us.APANNOTRECT;
    else if (anType === us.eAnTypes.ellipse) className = us.APANNOTELLIPSE;
    else if (anType === us.eAnTypes.arrow) className = us.APANNOTARROW;
    else if (anType === us.eAnTypes.pin) className = us.APANNOTPIN;
    else if (anType === us.eAnTypes.ruler) className = us.APANNOTCALIPER;
    else if (anType === us.eAnTypes.caliper) className = us.APANNOTCALIPER;
    else if (anType === us.eAnTypes.text) className = us.APANNOTTEXT;
    else if (anType === us.eAnTypes.polygon) className = us.APANNOTPOLY;
  }
  return className;
};

// getRandomColor - This function returns a random color selection from us.colorPalette (in hex RGB format)
us.getRandomColor = function () {
  var r = parseInt(Math.random() * us.colorPalette.length, 10);
  return (us.colorPalette[r]);
};

function isValidArrayIndex (array, idx) {
  return idx >= 0 && !!array[idx];
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/annotations/annotations-service.js